import { useState, useEffect, useCallback } from "react";
import {
  Building2, Save, Edit2, X, Check, RefreshCw,
  Phone, Mail, Shield, FileText, MapPin, User, Search, Lock, Plus
} from "lucide-react";

const API = `${getApiUrl()}/api`;
import getApiUrl from '../../utils/getApiUrl';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const EMPTY_FORM = {
  raison_sociale: "", forme_juridique: "", slogan: "",
  adresse: "", code_postal: "", ville: "",
  telephone: "", email: "", email_administratif: "",
  capital: "", siret: "", representant_legal: "",
  fonction_representant: "", assurance_decennale: "", tva_rate: 10.0,
};

function Field({ label, field, icon: Icon, type = "text", span = 1, form, setForm }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      <div className="flex items-center gap-2 bg-slate-900 rounded-lg border border-slate-700 px-3 py-2">
        {Icon && <Icon className="w-4 h-4 text-slate-500 flex-shrink-0" />}
        <input
          type={type}
          value={form[field]}
          onChange={(e) =>
            setForm((f) => ({
              ...f,
              [field]: type === "number" ? parseFloat(e.target.value) || 0 : e.target.value,
            }))
          }
          className="bg-transparent w-full text-sm text-white outline-none"
          placeholder={label}
        />
      </div>
    </div>
  );
}

export default function SocietesManager() {
  const [societes, setSocietes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [siretLookup, setSiretLookup] = useState("");
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/societes`);
      if (r.ok) setSocietes(await r.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-open edit form if exactly 1 société exists
  useEffect(() => {
    if (!loading && societes.length === 1 && !showForm && !editing) {
      startEdit(societes[0]);
    }
  }, [loading, societes.length]);

  const hasSociete = societes.length > 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      const url = editing
        ? `${API}/societes/${editing}`
        : `${API}/societes`;
      const r = await authFetch(url, {
        method: editing ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (r.ok) {
        setShowForm(false);
        setEditing(null);
        setForm(EMPTY_FORM);
        load();
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const startEdit = (s) => {
    setForm({
      raison_sociale: s.raison_sociale || "",
      forme_juridique: s.forme_juridique || "",
      slogan: s.slogan || "",
      adresse: s.adresse || "",
      code_postal: s.code_postal || "",
      ville: s.ville || "",
      telephone: s.telephone || "",
      email: s.email || "",
      email_administratif: s.email_administratif || "",
      capital: s.capital || "",
      siret: s.siret || "",
      representant_legal: s.representant_legal || "",
      fonction_representant: s.fonction_representant || "",
      assurance_decennale: s.assurance_decennale || "",
      tva_rate: s.tva_rate ?? 10.0,
    });
    setEditing(s.id);
    setSiretLookup("");
    setLookupError("");
    setShowForm(true);
  };

  const lookupSiret = async () => {
    const siret = siretLookup.replace(/\s/g, "");
    if (siret.length !== 14) {
      setLookupError("Le SIRET doit contenir 14 chiffres");
      return;
    }
    setLookingUp(true);
    setLookupError("");
    try {
      const r = await authFetch(`${API}/amo-mar/lookup-siret/${siret}`);
      if (r.ok) {
        const d = await r.json();
        setForm((prev) => ({
          ...prev,
          raison_sociale: d.nom_societe || d.nom_commercial || prev.raison_sociale,
          siret: d.siret || prev.siret,
          representant_legal: d.dirigeant?.split(" (")[0] || prev.representant_legal,
          fonction_representant: d.dirigeant?.match(/\((.+)\)/)?.[1] || prev.fonction_representant,
          adresse: d.adresse || prev.adresse,
          code_postal: d.code_postal || prev.code_postal,
          ville: d.ville || prev.ville,
        }));
      } else {
        const err = await r.json();
        setLookupError(err.detail || "Entreprise non trouvee");
      }
    } catch {
      setLookupError("Erreur de connexion");
    }
    setLookingUp(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Building2 className="w-6 h-6 text-amber-400" />
            Ma Societe
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Informations legales utilisees sur vos devis et factures
          </p>
        </div>
        <button onClick={load} className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Multi-société banner */}
      <div data-testid="multi-societe-banner" className="flex items-center gap-3 px-4 py-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
        <Lock className="w-5 h-5 text-amber-400 flex-shrink-0" />
        <div>
          <p className="text-sm text-amber-200 font-medium">Multi-societe — bientot disponible</p>
          <p className="text-xs text-slate-400 mt-0.5">
            La gestion de plusieurs entites juridiques depuis un meme espace est en cours de developpement.
          </p>
        </div>
      </div>

      {/* Form (edit existing or create first) */}
      {showForm && (
        <div className="bg-slate-800/80 border border-slate-700 rounded-xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-semibold text-white">
              {editing ? "Modifier la societe" : "Configurer votre societe"}
            </h3>
            {hasSociete && (
              <button
                onClick={() => { setShowForm(false); setEditing(null); }}
                className="p-1 hover:bg-slate-700 rounded"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            )}
          </div>

          {/* SIRET Lookup */}
          <div className="mb-5 p-4 bg-gradient-to-r from-blue-900/30 to-cyan-900/30 rounded-xl border border-blue-500/30">
            <label className="block text-xs text-blue-300 font-semibold mb-2">
              Remplissage automatique par SIRET
            </label>
            <div className="flex items-center gap-2">
              <input
                data-testid="societe-siret-lookup-input"
                value={siretLookup}
                onChange={(e) => { setSiretLookup(e.target.value); setLookupError(""); }}
                onKeyDown={(e) => e.key === "Enter" && lookupSiret()}
                placeholder="Entrez le SIRET (14 chiffres)"
                className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm font-mono tracking-wider"
              />
              <button
                data-testid="societe-siret-lookup-btn"
                onClick={lookupSiret}
                disabled={lookingUp || siretLookup.replace(/\s/g, "").length < 14}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white rounded-lg text-sm"
              >
                {lookingUp ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Rechercher
              </button>
            </div>
            {lookupError && <p className="text-xs text-red-400 mt-2">{lookupError}</p>}
            {!lookupError && form.raison_sociale && siretLookup && !lookingUp && (
              <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1">
                <Check className="w-3 h-3" /> Donnees recuperees pour {form.raison_sociale}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Raison sociale" field="raison_sociale" icon={Building2} form={form} setForm={setForm} />
            <Field label="Forme juridique (SAS, SARL...)" field="forme_juridique" icon={FileText} form={form} setForm={setForm} />
            <Field label="Slogan / Activite" field="slogan" span={2} form={form} setForm={setForm} />
            <Field label="Adresse" field="adresse" icon={MapPin} span={2} form={form} setForm={setForm} />
            <Field label="Code postal" field="code_postal" form={form} setForm={setForm} />
            <Field label="Ville" field="ville" icon={MapPin} form={form} setForm={setForm} />
            <Field label="Telephone" field="telephone" icon={Phone} form={form} setForm={setForm} />
            <Field label="Email contact" field="email" icon={Mail} form={form} setForm={setForm} />
            <Field label="Email administratif" field="email_administratif" icon={Mail} form={form} setForm={setForm} />
            <Field label="Capital social" field="capital" form={form} setForm={setForm} />
            <Field label="SIRET" field="siret" icon={Shield} form={form} setForm={setForm} />
            <Field label="Assurance decennale" field="assurance_decennale" icon={Shield} form={form} setForm={setForm} />
            <Field label="Representant legal" field="representant_legal" icon={User} form={form} setForm={setForm} />
            <Field label="Fonction" field="fonction_representant" form={form} setForm={setForm} />
            <Field label="Taux TVA par defaut (%)" field="tva_rate" type="number" form={form} setForm={setForm} />
          </div>

          <div className="flex justify-end gap-3 mt-5">
            {hasSociete && (
              <button
                onClick={() => { setShowForm(false); setEditing(null); }}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm"
              >
                Annuler
              </button>
            )}
            <button
              data-testid="save-societe-btn"
              onClick={handleSave}
              disabled={saving || !form.raison_sociale}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-lg text-sm transition-colors"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {editing ? "Mettre a jour" : "Creer"}
            </button>
          </div>
        </div>
      )}

      {/* Empty state — no société yet */}
      {!loading && !hasSociete && !showForm && (
        <div className="text-center py-12 border border-dashed border-slate-700 rounded-xl">
          <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-600" />
          <p className="text-slate-400 mb-4">Aucune societe configuree</p>
          <button
            data-testid="add-societe-btn"
            onClick={() => { setForm(EMPTY_FORM); setEditing(null); setSiretLookup(""); setLookupError(""); setShowForm(true); }}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Configurer ma societe
          </button>
          <p className="text-xs text-slate-500 mt-3">Utilisez votre SIRET pour un remplissage automatique</p>
        </div>
      )}

      {/* Société card (read-only view when not editing) */}
      {!loading && hasSociete && !showForm && (
        <div className="grid gap-4">
          {societes.map((s) => (
            <div
              key={s.id}
              data-testid={`societe-card-${s.id}`}
              className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 hover:border-amber-500/30 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-lg">
                        {s.forme_juridique} {s.raison_sociale}
                      </h3>
                      <p className="text-xs text-slate-400">{s.slogan}</p>
                    </div>
                    {s.actif && (
                      <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full flex items-center gap-1">
                        <Check className="w-3 h-3" /> Active
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3 text-sm">
                    <div className="flex items-center gap-2 text-slate-400">
                      <MapPin className="w-3.5 h-3.5" />
                      <span>{s.adresse}, {s.code_postal} {s.ville}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-400">
                      <Phone className="w-3.5 h-3.5" />
                      <span>{s.telephone}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-400">
                      <Shield className="w-3.5 h-3.5" />
                      <span>SIRET: {s.siret}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-400">
                      <User className="w-3.5 h-3.5" />
                      <span>{s.representant_legal} ({s.fonction_representant})</span>
                    </div>
                  </div>
                </div>
                <button
                  data-testid={`edit-societe-${s.id}`}
                  onClick={() => startEdit(s)}
                  className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors ml-4"
                >
                  <Edit2 className="w-4 h-4 text-blue-400" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-slate-500" />
        </div>
      )}
    </div>
  );
}
